<template>
  <div class="login-card">
    <div class="login-card__header">
      <h2 class="login-card__title">{{ cardTitle }}</h2>
      <p class="login-card__sub">{{ cardSub }}</p>
    </div>

    <div class="login-card__tabs" role="tablist">
      <button
        type="button"
        class="login-card__tab"
        :class="{ 'login-card__tab--active': !isCompany }"
        data-cy="user-tab"
        @click="goToLoginType(false)"
      >
        Profissional
      </button>
      <button
        type="button"
        class="login-card__tab"
        :class="{ 'login-card__tab--active': isCompany }"
        data-cy="company-tab"
        @click="goToLoginType(true)"
      >
        Empresa
      </button>
    </div>

    <form class="login-card__form" @submit.prevent="handleLogin">
      <InputField
        v-model="state.email.value"
        label="E-mail"
        type="email"
        placeholder="seu@email.com"
        data-cy="email"
        tabindex="1"
        :error="state.email.errorMessage"
      >
        <template #icon>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </template>
      </InputField>

      <InputField
        v-model="state.password.value"
        label="Senha"
        type="password"
        placeholder="••••••••••"
        data-cy="password"
        tabindex="2"
      >
        <template #icon>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </template>
      </InputField>

      <router-link :to="{ name: 'ResetPassword' }" class="login-card__forgot">
        Esqueceu sua senha?
      </router-link>

      <BaseButton
        type="submit"
        variant="primary"
        size="md"
        class="login-card__submit"
        data-cy="signIn"
        tabindex="3"
        :disabled="state.loading"
      >
        {{ state.loading ? 'Entrando...' : 'Entrar na plataforma →' }}
      </BaseButton>

      <div class="login-card__divider">
        <span class="login-card__divider-line" />
        <span class="login-card__divider-text">ou</span>
        <span class="login-card__divider-line" />
      </div>

      <p class="login-card__signup" data-cy="create-account">
        <span>Não tem conta?</span>
        <router-link
          :to="{ name: 'SignUp', params: { isCompany: isCompany ? 2 : 1 } }"
          class="login-card__signup-link"
        >
          Cadastre-se grátis →
        </router-link>
      </p>
    </form>
  </div>
</template>

<script>
import { useField } from 'vee-validate';
import { computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { event } from 'vue-gtag';
import loginService from '../../../../api/person/login';
import BaseButton from '../../../../components/ui/BaseButton.vue';
import InputField from '../../../../components/ui/InputField.vue';
import { NOT_FOUND } from '../../../../enums/errorEnums';
import cookies from '../../../../utils/cookies';
import validators from '../../../../utils/validators';

export default {
  components: {
    BaseButton,
    InputField,
  },
  name: 'LoginForm',
  props: {
    isModal: {
      type: Boolean,
      default: true,
    },
    isCompany: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const router = useRouter();
    const toast = useToast();
    const store = useStore();

    store.dispatch('setSignupType', { signupType: props.isCompany ? 2 : 1 });

    const { value: emailValue, errorMessage: emailErroMessage } = useField(
      'email',
      validators.validateEmptyAndEmail,
    );

    const { value: passwordValue, errorMessage: passwordErroMessage } = useField('password', validators.validateEmptyAndStrongPassword);

    const state = reactive({
      loading: false,
      email: {
        value: emailValue,
        errorMessage: emailErroMessage,
      },
      password: {
        value: passwordValue,
        errorMessage: passwordErroMessage,
      },
      props: {
        isCompany: props.isCompany,
      },
    });

    const cardTitle = computed(() => (props.isCompany ? 'Entrar como empresa' : 'Bem-vindo de volta'));
    const cardSub = computed(() => (props.isCompany
      ? 'Acesse o portal da sua empresa na Indicoo'
      : 'Acesse sua conta para gerenciar indicações'));

    const goToLoginType = (targetIsCompany) => {
      if (targetIsCompany === props.isCompany) return;
      router.push({ name: targetIsCompany ? 'LoginCompany' : 'Login' });
    };

    function parseJwt(token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      );

      return JSON.parse(jsonPayload);
    }

    async function handleLogin() {
      try {
        if (state.email.errorMessage) {
          toast.warning(`Ops... ${state.email.errorMessage}`);
          return;
        }

        if (!state.email.value || !state.password.value) {
          toast.warning('E-mail e senha são obrigatórios!');

          return;
        }
        await store.dispatch('cleanData');
        state.loading = true;
        const { data, errors } = await loginService.login({
          email: state.email.value,
          password: state.password.value,
          isCompany: state.props.isCompany,
        });

        if (errors) {
          if (errors.status === NOT_FOUND) {
            toast.warning(errors.statusText);
          } else {
            toast.error(errors.statusText);
          }
          return;
        }

        if (data && data.token) {
          const { token, result } = data;
          const { role } = parseJwt(token);
          let isAdmin = false;

          if (role.toLowerCase() === 'admin') {
            isAdmin = true
            store.commit('SET_IS_ADMIN', true);
          }

          cookies.setAuthCookies({ token });
          store.commit('SET_IS_LOGGED', true);

          await store.dispatch('setCurrentUser', {
            isCompany: state.props.isCompany,
            ...result,
          });
          await store.dispatch('validateLogged');
          emit('closeModal');

          let routeName = 'UserDashboard';
          if (state.props.isCompany) {
            routeName = 'CompanyDashboard';
            event('login_company', { method: 'Google' })
            await store.dispatch('getMeCompany', { email: '', force: true });
          } else if (isAdmin) {
            routeName = 'AdminDash';
            event('login_admin', { method: 'Google' })
          } else {
            event('login', { method: 'Google' })
            await store.dispatch('getMe', { email: '', force: true });
          }

          router.push({ name: routeName });
        }
      } catch (error) {
        toast.error('Ops... tivemos um problem ao tentar realizar o seu login');
      } finally {
        state.loading = false;
      }
    }

    return {
      state,
      emit,
      handleLogin,
      cardTitle,
      cardSub,
      goToLoginType,
    };
  },
};
</script>

<style scoped>
.login-card {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-lg);
  padding: 40px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
}

.login-card__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.login-card__title {
  font-family: var(--font-brand);
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--white);
}

.login-card__sub {
  font-family: var(--font-brand);
  font-size: 14px;
  color: var(--muted);
}

.login-card__tabs {
  display: flex;
  gap: 8px;
  padding: 4px;
  background: var(--navy-mid);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-pill);
}

.login-card__tab {
  flex: 1;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: var(--r-pill);
  font-family: var(--font-brand);
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  transition: 0.2s var(--ease);
}

.login-card__tab--active {
  background: var(--cyan);
  color: var(--navy-deep);
}

.login-card__form {
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
}

.login-card__forgot {
  align-self: flex-end;
  margin-top: -8px;
  font-family: var(--font-brand);
  font-size: 13px;
  font-weight: 600;
  color: var(--cyan);
  text-decoration: none;
}

.login-card__submit {
  width: 100%;
}

.login-card__submit:disabled {
  opacity: 0.7;
  cursor: default;
}

.login-card__divider {
  display: flex;
  align-items: center;
  gap: 12px;
}

.login-card__divider-line {
  flex: 1;
  height: 1px;
  background: var(--navy-border);
}

.login-card__divider-text {
  font-family: var(--font-brand);
  font-size: 13px;
  color: var(--faint);
}

.login-card__signup {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: var(--font-brand);
  font-size: 14px;
  color: var(--muted);
}

.login-card__signup-link {
  font-weight: 700;
  color: var(--cyan);
  text-decoration: none;
}
</style>
